import { useMemo, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  Pager,
  StateBlock,
  useToast,
  type Column,
} from '@w7/ui'
import { useViewerCurrency } from '@/api/hooks/shared/useViewerCurrency'
import { CmPanel } from '@/features/catalog/CmPanel'
import { count, EMPTY, lll24, money } from '@/lib/format'
import type { CreditLimitLogSource } from './clTransfersData'
import {
  calendarDayKey,
  resolveClTransfersClassicSource,
  validateClTransfersClassicDates,
} from './clTransfersSource'
import type { CreditLimitTransferRow } from './clTransfersTypes'
import { useCreditLimitTransfers } from './useCreditLimitTransfers'
import '@/features/reports/my-pnl.css'

/**
 * CL Transfers — Winner All Logs "CL Transfers" (Backend-UI parity).
 * Lab Player P/L date routing — today-only → PG search; else BQ (≤93 days).
 *
 * Credit-limit change history (from / to / new limit / balances / IP), not the
 * Reports → Transfer Statement give/take cash list.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE

/** Backend-UI `displayMoney` — values already in viewer display units. */
function displayMoney(
  value: number,
  currency: string,
  opts: { round?: boolean } = {},
): string {
  return money(value, { currency, alreadyDisplay: true, round: opts.round })
}

export function ClTransfersPage() {
  const toast = useToast()
  // Lab operator day (IST) — same key as resolveClTransfersClassicSource.
  const today = calendarDayKey(new Date())
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [page, setPage] = useState(1)
  const [applied, setApplied] = useState({ from: today, to: today, run: 1 })
  const currency = useViewerCurrency()
  const source: CreditLimitLogSource =
    resolveClTransfersClassicSource(applied.from, applied.to) === 'bq' ? 'bq' : 'postgres'
  const { data, isLoading, isFetching } = useCreditLimitTransfers(
    {
      page,
      pageSize: PAGE_SIZE,
      fromDate: applied.from,
      toDate: applied.to,
      run: applied.run,
    },
    source,
  )

  const onSearch = () => {
    const error = validateClTransfersClassicDates(fromDate, toDate)
    if (error) {
      toast.error(error)
      return
    }
    setPage(1)
    // Bump `run` so Search always refetches (same dates would otherwise keep the queryKey).
    setApplied((prev) => ({ from: fromDate, to: toDate, run: prev.run + 1 }))
  }

  const onFromChange = (value: string) => {
    setFromDate(value)
    if (toDate && value && toDate < value) {
      setToDate(value)
    }
  }

  const clearFilters = () => {
    const day = calendarDayKey(new Date())
    setFromDate(day)
    setToDate(day)
    setPage(1)
    setApplied((prev) => ({ from: day, to: day, run: prev.run + 1 }))
  }

  const filtersDirty =
    fromDate !== today ||
    toDate !== today ||
    applied.from !== today ||
    applied.to !== today

  const columns = useMemo<Column<CreditLimitTransferRow>[]>(
    () => [
      {
        id: 'createdAt',
        header: 'Creation Date',
        cell: (row) => lll24(row.createdAt),
      },
      {
        id: 'from',
        header: 'From',
        cell: (row) => row.fromUsername || EMPTY,
      },
      {
        id: 'to',
        header: 'To',
        cell: (row) => row.toUsername || EMPTY,
      },
      {
        id: 'creditLimit',
        header: 'CreditLimit',
        align: 'right',
        numeric: true,
        cell: (row) => displayMoney(row.creditLimit, currency, { round: true }),
      },
      {
        id: 'balance',
        header: 'Balance',
        align: 'right',
        numeric: true,
        cell: (row) => displayMoney(row.balance, currency),
      },
      {
        id: 'oldBalance',
        header: 'Old Balance',
        align: 'right',
        numeric: true,
        cell: (row) => displayMoney(row.oldBalance, currency),
      },
      {
        id: 'ip',
        header: 'IP',
        cell: (row) => row.clientIp || EMPTY,
      },
    ],
    [currency],
  )

  const unavailable = data?.unavailable
  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1)

  return (
    <CmPanel
      title="CL Transfers"
      subtitle="Today → live log; other dates → BigQuery (max 93 days) — who raised or lowered a line, and the balances around that edit"
      wide
    >
      <div className="cm-my-pnl">
        <div
          className="cm-my-pnl-filters"
          role="region"
          aria-label="CL Transfers filters"
        >
          <Field label="From Date" required>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="date"
                required
                max={today}
                value={fromDate}
                onChange={(e) => onFromChange(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date" required>
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="date"
                required
                min={fromDate || undefined}
                max={today}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            )}
          </Field>
          <div className="cm-my-pnl-filters-actions">
            <Button type="button" variant="primary" onClick={onSearch} busy={isFetching}>
              Search
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={clearFilters}
              disabled={!filtersDirty}
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {unavailable ? (
          <div style={{ marginBottom: 'var(--w7-s4)' }}>
            <StateBlock icon="info" title="No data found">
              {unavailable.reason}
              {unavailable.trackedBy ? ` Tracked by ${unavailable.trackedBy}.` : null}
            </StateBlock>
          </div>
        ) : null}

        <Card
          flush
          title="Credit-limit transfers"
          subtitle={
            isLoading
              ? 'Loading'
              : unavailable
                ? 'Source unavailable'
                : `${count(total)} in this list · ${applied.from} → ${applied.to}`
          }
          footer={
            unavailable ? null : (
              <Pager
                page={data?.page ?? page}
                pageCount={pageCount}
                onChange={setPage}
                total={total}
                pageSize={PAGE_SIZE}
              />
            )
          }
        >
          <DataTable
            label="Credit-limit transfers"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            loading={isLoading}
            skeletonRows={8}
            empty={
              unavailable ? (
                <StateBlock icon="sliders" title="Credit-limit log unavailable">
                  Live CM-API did not return credit-limit history.
                </StateBlock>
              ) : (
                <StateBlock icon="sliders" title="No credit-limit transfers">
                  Raising or lowering an account’s credit limit is recorded here when the log
                  source has entries for your seat.
                </StateBlock>
              )
            }
          />
        </Card>
      </div>
    </CmPanel>
  )
}
